'use strict';

import angular from 'angular';

/**
 * @ngdoc controller
 * @name observatoireApp.controller:AutorisationsExploitationDetailsController
 * @description
 * # AutorisationsExploitationDetailsController
 * Contrôleur pour la gestion des détails d'une autorisation d'exploitation
 */
angular.module('observatoireApp')
  .controller('AutorisationsExploitationDetailsController', ['$scope', '$stateParams', '$http', '$sce', 'MetaService',
    function ($scope, $stateParams, $http, $sce, MetaService) {
      
      // Initialisation des données
      $scope.autorisation = null;
      $scope.isLoading = true;
      $scope.error = null;
      
      /**
       * Charge les détails d'une autorisation d'exploitation
       * @param {number} id Identifiant de l'autorisation
       */
      $scope.chargerAutorisation = function(id) {
        $scope.isLoading = true;
        $scope.error = null;
        
        $http.get(`/api/autorisations-exploitation/${id}`)
          .then(function(response) {
            let autorisation = response.data;
            
            // Convertir les dates
            autorisation.datePermis = $scope.convertirDate(autorisation.datePermis);
            autorisation.dateDemande = $scope.convertirDate(autorisation.dateDemande);
            autorisation.dateDebutExploitation = $scope.convertirDate(autorisation.dateDebutExploitation);
            autorisation.dateExpirationPrevue = $scope.convertirDate(autorisation.dateExpirationPrevue);
            autorisation.dateExpirationProlongee = $scope.convertirDate(autorisation.dateExpirationProlongee);
            autorisation.dateResiliation = $scope.convertirDate(autorisation.dateResiliation);
            autorisation.dateDerniereModification = $scope.convertirDate(autorisation.dateDerniereModification);
            
            // Pour les documents dans la collection
            if (autorisation.documents) {
              autorisation.documents.forEach(function(doc) {
                if (doc.date) doc.date = $scope.convertirDate(doc.date);
              });
            }
            
            // S'assurer que les propriétés existent pour éviter les erreurs dans le frontend
            if (!autorisation.clauses) {
              autorisation.clauses = [];
            }
            
            if (!autorisation.historique) {
              autorisation.historique = [];
            }
            
            $scope.autorisation = autorisation;
            $scope.isLoading = false;
            
            // Mise à jour des métadonnées de la page avec les détails de l'autorisation
            if (autorisation) {
              const titre = autorisation.denomination ? 
                `${autorisation.denomination} - Autorisation d'exploitation` : 
                'Détails de l\'autorisation d\'exploitation';
              
              // Description détaillée pour les métadonnées
              let description = `Détails de l'autorisation d'exploitation pétrolière`;
              
              if (autorisation.denomination) {
                description += ` pour ${autorisation.denomination}`;
              }
              
              if (autorisation.gisement) {
                description += `, gisement ${autorisation.gisement}`;
              }
              
              if (autorisation.blocPetrolier) {
                description += ` dans le ${autorisation.blocPetrolier}`;
              }
              
              // Mots-clés spécifiques à cette autorisation
              let keywords = ['autorisation exploitation', 'pétrole', 'tchad'];
              
              if (autorisation.gisement) {
                keywords.push(autorisation.gisement);
              }
              
              if (autorisation.blocPetrolier) {
                keywords.push(autorisation.blocPetrolier);
              }
              
              if (autorisation.denomination) {
                keywords.push(autorisation.denomination);
              }
              
              // Mettre à jour les métadonnées via le service
              MetaService.setAll({
                title: titre,
                description: description,
                keywords: keywords
              });
            }
          })
          .catch(function(error) {
            console.error('Erreur lors du chargement des détails:', error);
            $scope.error = 'Impossible de charger les détails de l\'autorisation. Veuillez réessayer ultérieurement.';
            $scope.isLoading = false;
            
            // Si l'API échoue, créer une autorisation fictive pour éviter les erreurs d'affichage
            $scope.autorisation = {
              id: parseInt(id),
              denomination: 'Autorisation non disponible',
              reference: 'N/A',
              gisement: 'Non disponible',
              blocPetrolier: 'Non disponible',
              statut: 'Inconnu',
              datePermis: new Date(),
              dateExpirationPrevue: new Date(),
              participants: [],
              documents: []
            };
          });
      };
      
      /**
       * Convertit une valeur de date en objet Date
       * @param {*} dateVal Valeur de la date (à convertir)
       * @returns {Date|null} Objet Date ou null si impossible à convertir
       */
      $scope.convertirDate = function(dateVal) {
        if (!dateVal) return null;
        
        // Si dateVal est déjà un objet Date, le retourner directement
        if (dateVal instanceof Date) {
          return dateVal;
        }
        
        // Gérer le cas où dateVal est un tableau [année, mois, jour, ...]
        if (Array.isArray(dateVal)) {
          // Dans les tableaux, le mois est 1-indexed, mais dans Date() il est 0-indexed
          const moisIndex = dateVal[1] - 1; 
          return new Date(dateVal[0], moisIndex, dateVal[2], dateVal[3] || 0, dateVal[4] || 0);
        }
        
        // S'assurer que dateVal est une chaîne de caractères pour les formats textuels
        const dateString = typeof dateVal === 'string' ? dateVal : String(dateVal);
        
        // Format: yyyy-MM-dd
        if (typeof dateString === 'string') {
          const simpleDateMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
          if (simpleDateMatch) {
            return new Date(simpleDateMatch[1], simpleDateMatch[2]-1, simpleDateMatch[3]);
          }
        }
        
        // Format: yyyy-MM-dd HH:mm:ss
        if (typeof dateString === 'string') {
          const match = dateString.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
          if (match) {
            return new Date(match[1], match[2]-1, match[3], match[4], match[5], match[6]);
          }
        }
        
        // Essayer le constructeur Date standard en dernier recours
        const date = new Date(dateString);
        
        // Vérifier si la date est valide (pas NaN)
        if (!isNaN(date.getTime())) {
          return date;
        }
        
        return null;
      };
      
      /**
       * Formatte une date pour l'affichage
       * @param {Date|string|Array} date Date à formater
       * @returns {string} Date formatée
       */
      $scope.formatterDate = function(date) {
        if (!date) return 'N/A';
        
        if (date instanceof Date) {
          return date.toLocaleDateString('fr-FR');
        }
        
        if (Array.isArray(date)) {
          // Si c'est un tableau [année, mois, jour], formater manuellement
          return `${date[2]}/${date[1]}/${date[0]}`;
        }
        
        try {
          return new Date(date).toLocaleDateString('fr-FR');
        } catch (e) {
          return date.toString();
        }
      };
      
      /**
       * Rend du contenu HTML sécurisé pour l'affichage
       * @param {string} html Contenu HTML à sécuriser
       * @returns {Object} Contenu HTML sécurisé
       */
      $scope.renderHtml = function(html) {
        return $sce.trustAsHtml(html);
      };
      
      // Charger l'autorisation au démarrage si un ID est fourni
      if ($stateParams.id) {
        $scope.chargerAutorisation($stateParams.id);
      } else {
        $scope.error = 'Aucun identifiant d\'autorisation spécifié.';
        $scope.isLoading = false;
      }
    }])
  .name;
